// Subscription Service — real backend API integration
// All payment verification and subscription activation happens server-side.
// The frontend NEVER simulates or bypasses payment completion.
//
// All URLs come from environment variables via platform/config
// (Amendment A12 — no hardcoded URLs, no fallbacks).
import { config } from './platform/config';

// ── API Base URL (from env) ──────────────────────────────────────────────────
const API_BASE = config.API_BASE;

// ── Development Mode (bypasses backend for testing) ───────────────────────────
export const DEV_MODE = config.DEV_MODE;

// ── Faida Backend URL (ClickPesa + Supabase backend) ─────────────────────────
export const BACKEND_URL = config.BACKEND_URL;

// ── ClickPesa Hosted Checkout URL (optional) ──────────────────────────────────
export const CLICKPESA_CHECKOUT_URL = config.CLICKPESA_CHECKOUT_URL;

// ── Fetch live subscription status from backend after payment ─────────────────
// SECURITY: the backend now derives the email from the caller's Supabase
// access token and ignores any email query param, so a user can only read
// their OWN subscription. Pass the logged-in user's accessToken.
export const fetchSubscriptionStatus = async (email, accessToken) => {
  try {
    if (!accessToken) return null; // backend requires an authenticated caller
    const res = await fetch(`${BACKEND_URL}/subscription`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data || data.subscription_status !== 'active') return null;
    return {
      plan: data.subscription_plan || 'monthly',
      status: data.subscription_status,
      startDate: data.updated_at,
      endDate: data.subscription_expires_at,
      activatedAt: data.updated_at,
      trialUsed: true,
    };
  } catch (e) {
    console.error('fetchSubscriptionStatus error:', e);
    return null;
  }
};

// ── Plan Definitions ─────────────────────────────────────────────────────────
// oldPrice = the crossed-out anchor price shown before the real price.
// Edit oldPrice/price freely — the strikethrough and "Save X%" chip on the
// subscription page recalculate automatically. Set oldPrice to null to show
// the plain price with no strikethrough.
export const PLANS = [
  { id: 'faida_basic',   name: 'Basic',   price: 12000, oldPrice: 15000,  currency: 'TSh', duration: '1 Month',   days: 30,  badge: null,           accent: '#A3E635' },
  { id: 'faida_growth',  name: 'Growth',  price: 30000, oldPrice: 40000,  currency: 'TSh', duration: '3 Months',  days: 90,  badge: 'Most Popular', accent: '#FACC15' },
  { id: 'faida_pro',     name: 'Pro',     price: 55000, oldPrice: 75000,  currency: 'TSh', duration: '6 Months',  days: 180, badge: 'Best Value',   accent: '#38BDF8' },
  { id: 'faida_premium', name: 'Premium', price: 95000, oldPrice: 140000, currency: 'TSh', duration: '12 Months', days: 365, badge: 'Save More',    accent: '#C084FC' },
];

export const TRIAL_DAYS = 7;

// ── Local subscription cache (read-only fallback) ────────────────────────────
const SUB_KEY = (uid) => `faida_${uid}_subscription`;

export const loadSubscription = (uid) => {
  try {
    const raw = localStorage.getItem(SUB_KEY(uid));
    if (raw) return JSON.parse(raw);
  } catch {}
  return null;
};

export const saveSubscription = (uid, sub) => {
  localStorage.setItem(SUB_KEY(uid), JSON.stringify(sub));
};

// ── Initialize trial subscription for new user ──────────────────────────────
export const initTrialSubscription = (uid) => {
  const existing = loadSubscription(uid);
  if (existing) return existing;

  const now = new Date();
  const sub = {
    plan: 'trial',
    status: 'active',
    startDate: now.toISOString(),
    endDate: new Date(now.getTime() + TRIAL_DAYS * 86400000).toISOString(),
    paymentId: null,
    trialUsed: true,
  };
  saveSubscription(uid, sub);
  return sub;
};

// ── Check if subscription is active ──────────────────────────────────────────
export const isSubscriptionActive = (sub) => {
  if (!sub) return false;
  if (sub.status === 'cancelled') return false;
  const now = new Date();
  const end = new Date(sub.endDate);
  return now < end;
};

// ── Get days remaining ───────────────────────────────────────────────────────
export const getDaysRemaining = (sub) => {
  if (!sub) return 0;
  const now = new Date();
  const end = new Date(sub.endDate);
  const diff = end - now;
  return Math.max(0, Math.ceil(diff / 86400000));
};

// ── API helper ───────────────────────────────────────────────────────────────
const apiFetch = async (path, options = {}) => {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error || data.message || `Request failed (${res.status})`);
  }
  return data;
};

// ── Create Payment ───────────────────────────────────────────────────────────
// POST /api/payments/create
// Body: { planId, userId, phoneNumber }
// Returns: { paymentId, status }
export const createPayment = async (uid, planId, phoneNumber) => {
  const plan = PLANS.find(p => p.id === planId);
  if (!plan) throw new Error('Invalid plan');

  // Development mode: bypass backend and return mock payment
  if (DEV_MODE) {
    console.warn('⚠️ Running in DEV_MODE: payment is simulated');
    return {
      paymentId: `dev_${Date.now()}`,
      status: 'pending',
    };
  }

  const data = await apiFetch('/api/payments/create', {
    method: 'POST',
    body: JSON.stringify({ planId, userId: uid, phoneNumber, amount: plan.price, currency: plan.currency }),
  });

  return {
    paymentId: data.paymentId,
    status: data.status || 'pending',
  };
};

// ── Verify Payment ───────────────────────────────────────────────────────────
// GET /api/payments/verify/:paymentId
// Returns: { verified, status, payment? }
// SECURITY: Backend checks with payment provider — frontend never decides.
export const verifyPayment = async (uid, paymentId) => {
  // Development mode: auto-verify all payments
  if (DEV_MODE) {
    console.warn('⚠️ Running in DEV_MODE: payment verification is simulated');
    return {
      verified: true,
      status: 'completed',
    };
  }

  const data = await apiFetch(`/api/payments/verify/${paymentId}?userId=${encodeURIComponent(uid)}`);
  return data; // { verified: bool, status: string, payment?: {...} }
};

// ── Poll for payment verification ────────────────────────────────────────────
// After createPayment, the user completes payment on their phone.
// We poll the backend to check when the payment provider confirms it.
// maxAttempts × intervalMs = total wait time (default ~2 minutes)
export const pollPaymentVerification = async (uid, paymentId, { maxAttempts = 24, intervalMs = 5000, onStatus } = {}) => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const result = await verifyPayment(uid, paymentId);

    if (onStatus) onStatus(result.status || (result.verified ? 'completed' : 'pending'));

    if (result.verified) return result;

    // If backend explicitly says failed/cancelled, stop polling
    if (result.status === 'failed' || result.status === 'cancelled') {
      throw new Error(result.reason || 'Payment was declined or cancelled');
    }

    // Wait before next poll
    await new Promise(r => setTimeout(r, intervalMs));
  }

  throw new Error('Payment verification timed out. If you completed the payment, please check back later.');
};

// ── Activate Subscription ────────────────────────────────────────────────────
// POST /api/subscriptions/activate
// Body: { paymentId, userId }
// Backend verifies the payment, creates subscription record, returns sub data.
// The frontend does NOT calculate dates — the backend is authoritative.
export const activateSubscription = async (uid, paymentId) => {
  // Development mode: immediately activate a 1-year subscription
  if (DEV_MODE) {
    console.warn('⚠️ Running in DEV_MODE: subscription activated for 1 year (simulated)');
    const now = new Date();
    const sub = {
      plan: 'faida_growth', // Default to Growth plan in dev mode
      status: 'active',
      startDate: now.toISOString(),
      endDate: new Date(now.getTime() + 365 * 86400000).toISOString(),
      paymentId: paymentId,
      trialUsed: true,
      activatedAt: now.toISOString(),
    };
    saveSubscription(uid, sub);
    return sub;
  }

  const data = await apiFetch('/api/subscriptions/activate', {
    method: 'POST',
    body: JSON.stringify({ paymentId, userId: uid }),
  });

  // data should contain: { plan, status, startDate, endDate, paymentId, activatedAt }
  const sub = {
    plan: data.plan,
    status: data.status || 'active',
    startDate: data.startDate,
    endDate: data.endDate,
    paymentId: data.paymentId || paymentId,
    trialUsed: true,
    activatedAt: data.activatedAt || new Date().toISOString(),
  };

  // Cache locally for offline status display
  saveSubscription(uid, sub);
  return sub;
};
